import { ArrowDown } from "lucide-react";
import { PageHero } from "@/components/common/PageHero";
import { SectionHeading } from "@/components/common/SectionHeading";
import { Reveal } from "@/components/common/Reveal";
import { GoldButton } from "@/components/common/Buttons";
import { CessionForm } from "@/components/forms/CessionForm";
import { PROCESS, CRITERIA, IMAGES } from "@/lib/content";
import { useSeo } from "@/lib/seo";

export default function Ceder() {
  useSeo({
    title: "Céder votre entreprise — Transmission confidentielle | Losta Group",
    description:
      "Vous souhaitez vendre ou transmettre votre entreprise (enseigne, signalétique, impression, covering, métallerie) ? Losta Group étudie chaque projet confidentiellement, en France et en Suisse.",
    path: "/ceder-votre-entreprise",
  });

  return (
    <>
      <PageHero
        eyebrow="Céder votre entreprise"
        lines={["Vous avez construit", "votre entreprise.", "Écrivons la suite."]}
        sub="Céder une entreprise, ce n'est pas transmettre des machines ou un chiffre d'affaires. C'est transmettre des années de travail, une réputation, une équipe et un savoir-faire."
        image={IMAGES.workshop}
      >
        <GoldButton href="#formulaire" testid="ceder-hero-cta">
          Parler de mon entreprise <ArrowDown className="h-4 w-4" />
        </GoldButton>
      </PageHero>

      {/* Intro */}
      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 grid gap-12 lg:grid-cols-2 lg:gap-20">
          <SectionHeading eyebrow="Notre engagement" lines={["Une approche directe", "et confidentielle."]} />
          <div className="space-y-5 text-base sm:text-lg text-[var(--txt-2)] font-light leading-relaxed">
            <Reveal><p>Losta Group souhaite inscrire ses acquisitions dans une logique de continuité et de développement.</p></Reveal>
            <Reveal delay={0.05}><p>Notre objectif est de préserver les compétences existantes, d'apporter de nouveaux moyens et de créer des synergies avec les autres entreprises du groupe.</p></Reveal>
            <Reveal delay={0.1}><p className="text-slate-200">Chaque projet est étudié individuellement. Premier échange confidentiel et sans engagement.</p></Reveal>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 sm:py-28 bg-[#0B0D13] border-y border-white/10">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <SectionHeading center eyebrow="Le processus" lines={["De l'échange", "à la transmission."]} className="mb-16" />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {PROCESS.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.06}>
                <div className="group h-full rounded-2xl border border-white/10 card-lux p-7">
                  <div className="flex items-baseline justify-between">
                    <span className="font-serif text-5xl text-gold/80">{p.n}</span>
                    <span className="h-px w-10 bg-white/10 group-hover:bg-[var(--gold)]/60 transition-colors" />
                  </div>
                  <h3 className="mt-6 font-serif text-2xl text-white">{p.title}</h3>
                  <p className="mt-3 text-sm text-[var(--txt-2)] font-light leading-relaxed">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Criteria */}
      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <SectionHeading
            eyebrow="Nos critères"
            lines={["Nous ne recherchons pas", "simplement des entreprises.", "Nous recherchons des savoir-faire."]}
            className="mb-16"
          />
          <div className="divide-y divide-white/10 border-y border-white/10">
            {CRITERIA.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.05}>
                <div className="group grid grid-cols-1 md:grid-cols-[auto_1fr_2fr] gap-4 md:gap-10 items-baseline py-7 px-2 -mx-2 rounded-lg hover:bg-white/[0.02] transition-colors">
                  <span className="font-mono text-sm text-gold">{c.n}</span>
                  <h3 className="font-serif text-2xl sm:text-3xl text-white group-hover:text-gold transition-colors">{c.title}</h3>
                  <p className="text-base text-[var(--txt-2)] font-light leading-relaxed">{c.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <p className="mt-8 text-xs text-slate-500 max-w-xl">
            Aucun critère financier précis (CA, EBITDA, valorisation) n'est affiché tant qu'il n'a pas été défini avec vous.
          </p>
        </div>
      </section>

      {/* Form */}
      <section id="formulaire" className="py-20 sm:py-28 bg-[#0B0D13] border-t border-white/10 scroll-mt-24">
        <div className="mx-auto max-w-[900px] px-5 sm:px-8">
          <SectionHeading center eyebrow="Formulaire confidentiel" lines={["Parlons de", "votre entreprise."]} className="mb-12" />
          <CessionForm />
        </div>
      </section>
    </>
  );
}
